import type { DocPageContent } from '../docs-content'

const page = {
  order: 24,
  slug: 'tips-and-tricks',
  title: '使用提示与技巧',
  description: '日常剪辑中总结的实用技巧，涵盖编辑器总览、素材管理、AI 功能和导出。',
  category: '参考',
  sections: [
    {
      title: '编辑器总览',
      items: [
        '顶部工具栏放着保存、导出、设置、会员和帮助这些常用入口。',
        '左侧面板适合导入素材、生成配音、做字幕和使用 AI 功能。',
        '中间预览区用来看当前画面是否正常、字幕是否合适、播放是否顺畅。',
        '底部时间线是你真正剪视频、排顺序、卡节奏的地方。',
        '先把项目分辨率和素材准备好，再开始往时间线里放内容。',
        '如果要生成 AI 配音、字幕或音乐，建议先在左侧做完，再统一回到时间线处理。',
        '导出前先完整预览一遍，确认画面、字幕和声音都没问题。',
      ],
    },
    {
      title: '本地工作区与项目',
      items: [
        '快剪的项目和素材主要保存在本地，更适合反复修改和继续编辑。',
        '保存后的项目，之后可以继续打开接着做。',
        '如果浏览器提示没有目录权限，重新授权一次工作区就可以。',
        '缓存和本地模型相关状态，可以在设置里查看。',
        '保存项目只是保存编辑进度，不会自动导出成视频。',
        '如果要换电脑或换浏览器，建议先备份工作区或导出项目。',
        '如果原素材被移动或改名，重新打开项目时可能需要重新定位素材。',
      ],
    },
    {
      title: '素材导入与媒体库',
      items: [
        '不管是本地视频、图片、音频，还是 AI 生成的配音和字幕，都建议先整理到媒体库。',
        '导入后先看看素材方向、时长和预览是不是正常。',
        'AI 生成的结果建议先保存，再决定要不要插入时间线。',
        '素材一多，先整理名字和用途，后面会省很多时间。',
        '先生成语音后，直接选择"保存并插入"会更省事。',
      ],
    },
    {
      title: '时间线基础剪辑',
      items: [
        '把素材拖进时间线后，再去右侧细调位置、大小和透明度。',
        '操作前先看清你选中的是片段、轨道，还是时间线空白区。',
        '项目越复杂，越建议边剪边保存。',
        '拖动片段，调整出场顺序和出现时间。',
        '用裁剪或分割去掉多余部分，只保留重点内容。',
        '结合波形和预览，检查口播、字幕和音乐有没有对上。',
        '对话类内容可以先转字幕，再回头精修节奏。',
      ],
    },
    {
      title: '预览区与属性面板',
      items: [
        '预览区用来看效果，属性面板用来改细节——这两个区域通常要配合着用。',
        '先选中对象，再看右侧是不是已经切到了对应设置。',
        '画布大小、背景和项目时长这些信息，也可以顺手在这里确认。',
        '调整视频或图片的位置、缩放、裁切和旋转。',
        '确认转场、标记和片段属性有没有按预期生效。',
      ],
    },
    {
      title: '文字转语音（AI 配音）',
      items: [
        '快剪英文模型更适合英文内容，多语言模型更适合中文和多语言口播。',
        '生成后可以先试听，满意了再保存或插入时间线。',
        '长文案建议分段生成，后面在时间线上会更好调整节奏。',
        '如果生成失败，可以先检查网络或缓存状态，再重新试一次。',
      ],
    },
    {
      title: '字幕与转录',
      items: [
        '转录功能可以先快速整理口播内容，提高剪辑效率。',
        '先生成字幕骨架，再修语气词、专有名词和断句会更轻松。',
        '长音频建议分段转录，后面更好检查。',
        '先把口播转成字幕，再根据字幕节奏剪掉停顿和空白。',
        '有专有名词、品牌名或人名时，建议重点复核。',
      ],
    },
    {
      title: '导出设置',
      items: [
        '导出前先想清楚你是要导出整条视频，还是只导出其中一段。',
        '有些格式和清晰度组合，会受到浏览器能力影响。',
        '如果已经知道要发到哪个平台，按平台要求来选会更稳。',
        '画面比例、字幕位置和封面画面确认都正常。',
        '声音大小是否合适，有没有突然静音或爆音。',
      ],
    },
    {
      title: '排查常见问题',
      items: [
        '导出失败：先确认你选的清晰度、格式和质量是不是当前浏览器支持的组合。',
        'AI 模型慢：第一次使用 AI 功能会先下载模型，慢一些是正常的。',
        '字幕不准：自动转录更适合作为初稿，关键内容建议人工确认。',
        '如果多次失败，可以清理相关缓存后重新尝试。',
        '如果确认网络正常但还是不行，可以直接发邮件说明问题。',
      ],
    },
  ],
} satisfies DocPageContent

export default page
